import type { User, UserRole, Opportunity, Application, Band, Message, Notification, Comment, Lineup, FlyerConfig, SavedFlyer, ChurnReason, Session } from '../types';

const apiFetch = async (url: string, options: RequestInit = {}) => {
    const response = await fetch(url, options);
    
    if (response.status === 204) return;

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (!response.ok) {
            const error = new Error(data.error || 'API Request failed') as any;
            error.status = response.status;
            throw error;
        }
        return data;
    } else {
        const text = await response.text();
        if (!response.ok) {
            const error = new Error(text || `Server error: ${response.status}`) as any;
            error.status = response.status;
            throw error;
        }
        return text;
    }
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('youramp-token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
};

// --- AUTH ---
export const login = (handle: string, password: string): Promise<{ user: User; token: string }> =>
    apiFetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle, password }) });

export const loginWithGoogle = (idToken: string): Promise<{ user: User; token: string }> =>
    apiFetch('/api/auth/google', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken }) });

export const loginWithFacebook = (accessToken: string): Promise<{ user: User; token: string }> =>
    apiFetch('/api/auth/facebook', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accessToken }) });

export const register = (name: string, handle: string, email: string, password: string): Promise<{ user: User; token: string }> =>
    apiFetch('/api/auth/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, handle, email, password }) });

export const requestPasswordReset = (handle: string): Promise<{ success: boolean }> =>
    apiFetch('/api/auth/request-reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle }) });

export const confirmPasswordReset = (token: string, handle: string, newPassword: string): Promise<{ success: boolean }> =>
    apiFetch('/api/auth/confirm-reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, handle, newPassword }) });

export const completeOnboarding = (roles: UserRole[], referralCode?: string, newHandle?: string): Promise<{ user: User; token?: string }> =>
    apiFetch('/api/auth/onboard', { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ roles, referralCode, newHandle }) });

// --- USERS ---
export const getCurrentUser = (): Promise<User> => apiFetch('/api/users/current', { headers: getAuthHeaders() });
export const getUsers = (lastDocId?: string, limit: number = 50): Promise<{ users: User[], lastDocId: string | null, hasMore: boolean }> =>
    apiFetch(`/api/users?limit=${limit}${lastDocId ? `&lastDocId=${lastDocId}` : ''}`, { headers: getAuthHeaders() });
export const getUserByHandle = (handle: string): Promise<User> => apiFetch(`/api/users/${handle}`, { headers: getAuthHeaders() });
export const updateCurrentUser = (userData: Partial<User>): Promise<{ user: User; token?: string }> =>
    apiFetch('/api/users/current', { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(userData) });
export const changePassword = (currentPassword: string, newPassword: string): Promise<{ message: string }> =>
    apiFetch('/api/users/current/change-password', { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ currentPassword, newPassword }) });
export const endorseUser = (handle: string): Promise<{ success: boolean }> => apiFetch(`/api/users/${handle}/endorse`, { method: 'POST', headers: getAuthHeaders() });
export const unendorseUser = (handle: string): Promise<{ success: boolean }> => apiFetch(`/api/users/${handle}/endorse`, { method: 'DELETE', headers: getAuthHeaders() });
export const toggleFan = (handle: string): Promise<{ success: boolean }> => apiFetch(`/api/users/${handle}/fan`, { method: 'POST', headers: getAuthHeaders() });
export const freezeAccount = (reason: ChurnReason): Promise<{ message: string }> => apiFetch('/api/users/current/freeze', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ reason }) });
export const deleteAccount = (reason: ChurnReason): Promise<{ message: string }> => apiFetch('/api/users/current/delete', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ reason }) });

// --- OPPORTUNITIES & APPLICATIONS ---
export const createGuestRsvp = (opportunityId: string, data: { firstName: string; lastName: string; email: string }): Promise<{ success: boolean; rsvpId: string }> =>
    apiFetch(`/api/opportunities/${opportunityId}/guest-rsvp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const confirmGuestRsvp = (opportunityId: string, rsvpId: string, code: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/opportunities/${opportunityId}/guest-rsvp/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rsvpId, code }) });
export const getPublicOpportunities = (lastDocId?: string, limit: number = 24): Promise<{ opportunities: any[], lastDocId: string | null, hasMore: boolean }> =>
    apiFetch(`/api/opportunities/public?limit=${limit}${lastDocId ? `&lastDocId=${lastDocId}` : ''}`);
export const getOpportunities = (lastDocId?: string, limit: number = 20): Promise<{ opportunities: Opportunity[], lastDocId: string | null, hasMore: boolean }> =>
    apiFetch(`/api/opportunities?limit=${limit}${lastDocId ? `&lastDocId=${lastDocId}` : ''}`, { headers: getAuthHeaders() });
export const getMyApplications = (): Promise<{ application: Application, opportunity: Opportunity }[]> =>
    apiFetch('/api/opportunities/my-applications', { headers: getAuthHeaders() });
export const createOpportunity = (opportunityData: any): Promise<Opportunity> =>
    apiFetch('/api/opportunities', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(opportunityData) });
export const updateOpportunity = (id: string, opportunityData: any): Promise<Opportunity> =>
    apiFetch(`/api/opportunities/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(opportunityData) });
export const deleteOpportunity = (id: string): Promise<void> => apiFetch(`/api/opportunities/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
export const closeOpportunity = (id: string): Promise<void> => apiFetch(`/api/opportunities/${id}/close`, { method: 'POST', headers: getAuthHeaders() });
export const likeOpportunity = (id: string): Promise<{ success: boolean }> => apiFetch(`/api/opportunities/${id}/like`, { method: 'POST', headers: getAuthHeaders() });
export const amplifyOpportunity = (id: string): Promise<{ success: boolean }> => apiFetch(`/api/opportunities/${id}/amplify`, { method: 'POST', headers: getAuthHeaders() });
export const attendOpportunity = (id: string): Promise<{ success: boolean }> => apiFetch(`/api/opportunities/${id}/attend`, { method: 'POST', headers: getAuthHeaders() });
export const getComments = (id: string): Promise<Comment[]> => apiFetch(`/api/opportunities/${id}/comments`, { headers: getAuthHeaders() });
export const postComment = (id: string, text: string): Promise<Comment> =>
    apiFetch(`/api/opportunities/${id}/comments`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ text }) });
export const getApplicationsForOpportunity = (id: string): Promise<Application[]> => apiFetch(`/api/opportunities/${id}/applications`, { headers: getAuthHeaders() });
export const createApplication = (oppId: string, appData: any): Promise<Application> =>
    apiFetch(`/api/opportunities/${oppId}/applications`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(appData) });
export const deleteApplication = (oppId: string, appId: string): Promise<void> => apiFetch(`/api/opportunities/${oppId}/applications/${appId}`, { method: 'DELETE', headers: getAuthHeaders() });
export const rejectApplication = (oppId: string, appId: string): Promise<{ success: boolean }> => apiFetch(`/api/opportunities/${oppId}/applications/${appId}/reject`, { method: 'PUT', headers: getAuthHeaders() });
export const shortlistApplicant = (oppId: string, appId: string): Promise<{ success: boolean }> => apiFetch(`/api/opportunities/${oppId}/applications/${appId}/shortlist`, { method: 'PUT', headers: getAuthHeaders() });
export const acceptApplicant = (oppId: string, applicantId: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/opportunities/${oppId}/accept`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ applicantId }) });

// --- MESSAGES & NOTIFICATIONS ---
export const getMessages = (since?: string): Promise<Message[]> => apiFetch(`/api/messages${since ? `?since=${since}` : ''}`, { headers: getAuthHeaders() });
export const sendMessage = (messageData: { receiverHandle: string; text: string }): Promise<Message> =>
    apiFetch('/api/messages', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(messageData) });
export const markConversationAsRead = (partnerHandle: string): Promise<{ success: boolean }> =>
    apiFetch('/api/messages/read', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ partnerHandle }) });
export const getNotifications = (since?: string): Promise<Notification[]> => apiFetch(`/api/notifications${since ? `?since=${since}` : ''}`, { headers: getAuthHeaders() });
export const markNotificationsRead = (): Promise<{ success: boolean }> => apiFetch('/api/notifications/read', { method: 'POST', headers: getAuthHeaders() });

// --- BANDS ---
export const getBands = (lastDocId?: string, limit: number = 30): Promise<{ bands: Band[], lastDocId: string | null, hasMore: boolean }> =>
    apiFetch(`/api/bands?limit=${limit}${lastDocId ? `&lastDocId=${lastDocId}` : ''}`, { headers: getAuthHeaders() });
export const getBandById = (id: string): Promise<Band> => apiFetch(`/api/bands/${id}`, { headers: getAuthHeaders() });

export const createBand = (name: string, city: string, region: string, country: string): Promise<Band> =>
    apiFetch('/api/bands', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ name, city, region, country }) });

export const updateBand = (id: string, bandData: Partial<Band>): Promise<Band> =>
    apiFetch(`/api/bands/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(bandData) });

export const claimBand = (id: string): Promise<Band> => apiFetch(`/api/bands/${id}/claim`, { method: 'POST', headers: getAuthHeaders() });
export const toggleBandFan = (id: string): Promise<{ success: boolean }> => apiFetch(`/api/bands/${id}/fan`, { method: 'POST', headers: getAuthHeaders() });
export const addBandMember = (bandId: string, handle: string): Promise<Band> =>
    apiFetch(`/api/bands/${bandId}/members`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ handle }) });

export const removeBandMember = (bandId: string, handle: string): Promise<Band> =>
    apiFetch(`/api/bands/${bandId}/members`, { method: 'DELETE', headers: getAuthHeaders(), body: JSON.stringify({ handle }) });

// --- LINEUPS & FLYERS ---
export const getLineupOpportunities = (): Promise<Opportunity[]> => apiFetch('/api/lineups', { headers: getAuthHeaders() });
export const getLineupForOpportunity = (opportunityId: string): Promise<Lineup | null> => apiFetch(`/api/lineups/${opportunityId}`, { headers: getAuthHeaders() });
export const saveLineup = (opportunityId: string, lineupData: Partial<Lineup>): Promise<Lineup> =>
    apiFetch(`/api/lineups/${opportunityId}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(lineupData) });

export const getSavedFlyers = (): Promise<SavedFlyer[]> => apiFetch('/api/flyers', { headers: getAuthHeaders() });
export const saveFlyer = (flyerData: { opportunityId: string; config: FlyerConfig; lineup: any }): Promise<SavedFlyer> =>
    apiFetch('/api/flyers', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(flyerData) });

export const deleteSavedFlyer = (id: string): Promise<void> => apiFetch(`/api/flyers/${id}`, { method: 'DELETE', headers: getAuthHeaders() });

// --- SESSIONS (SOLUZIONE B: DIRECT UPLOAD) ---
export const getSessions = (): Promise<Session[]> => apiFetch('/api/sessions', { headers: getAuthHeaders() });

export const deleteSession = (id: string): Promise<void> => 
    apiFetch(`/api/sessions/${id}`, { method: 'DELETE', headers: getAuthHeaders() });

export const publishSession = async (data: { 
    videoBlob: Blob, 
    fileName: string, 
    title: string, 
    description: string, 
    type: string, 
    layout: string,
    parentSessionId?: string 
}): Promise<Session> => {
    // 1. Chiediamo le credenziali di upload al NOSTRO server
    const config = await apiFetch('/api/sessions/config', { headers: getAuthHeaders() });
    
    const { storageZone, accessKey, region } = config;
    const baseUrl = region === 'de' 
        ? `https://storage.bunnycdn.com/${storageZone}/` 
        : `https://${region}.storage.bunnycdn.com/${storageZone}/`;

    const videoPath = `sessions/${data.fileName}`;
    const uploadUrl = `${baseUrl}${videoPath}`;

    // 2. Upload DIRETTO dal browser a Bunny (Bypassa i limiti di Vercel)
    const bunnyResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'AccessKey': accessKey,
            'Content-Type': data.videoBlob.type || 'video/webm',
            'Accept': '*/*'
        },
        body: data.videoBlob
    });

    if (!bunnyResponse.ok) {
        throw new Error('Direct upload to storage failed.');
    }

    // 3. Salviamo i METADATI sul nostro database
    const storedPath = `bunny/${videoPath}`;
    
    return await apiFetch('/api/sessions', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            videoUrl: storedPath,
            title: data.title,
            description: data.description,
            type: data.type,
            layout: data.layout,
            parentSessionId: data.parentSessionId || null
        })
    });
};

// --- AI & FEEDBACK ---
export const generateOpportunityDescription = (title: string, type: string, keywords: string): Promise<string> =>
    apiFetch('/api/ai/generate-description', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ title, type, keywords }) }).then(res => res.description);
export const generateAiImage = (prompt: string): Promise<string> =>
    apiFetch('/api/ai/generate-image', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ prompt }) }).then(res => res.imageData);
export const generateCoverLetter = (user: User, opportunity: Opportunity, keywords: string): Promise<string> =>
    apiFetch('/api/ai/generate-cover-letter', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ opportunity, keywords }) }).then(res => res.coverLetter);
export const processNaturalLanguageSearchQuery = (query: string): Promise<any> =>
    apiFetch('/api/ai/process-search', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ query }) });
export const postFeedback = (text: string): Promise<{ success: boolean }> =>
    apiFetch('/api/feedback', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ text }) });
export const getMyFeedback = (): Promise<{ items: { id: string; text: string; timestamp: string | null }[] }> =>
    apiFetch('/api/feedback/mine', { headers: getAuthHeaders() });
export const logProfileView = (handle: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/users/${handle}/view`, { method: 'POST', headers: getAuthHeaders() });

// --- DASHBOARDS ---
export const getAdminDashboardStats = (): Promise<any> => apiFetch('/api/dashboards/admin', { headers: getAuthHeaders() });
export const getAdminUsersByFilter = (filter: string): Promise<{ users: any[] }> => apiFetch(`/api/dashboards/admin/users?filter=${filter}`, { headers: getAuthHeaders() });
export const getProDashboardStats = (): Promise<any> => apiFetch('/api/dashboards/pro', { headers: getAuthHeaders() });
export const getSeedDashboardStats = (): Promise<any> => apiFetch('/api/dashboards/seed', { headers: getAuthHeaders() });

// --- EMAIL VERIFICATION ---
export const resendVerification = (): Promise<{ success: boolean }> =>
    apiFetch('/api/auth/send-verification', { method: 'POST', headers: getAuthHeaders() });
export const verifyEmail = (token: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, { headers: getAuthHeaders() });
